import { useMemo } from 'react';
import {
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	WRAPPED_SOL_MINT,
} from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import useGetOraclePriceForMarket from './useGetOraclePriceForMarket';
import { useUniqueSpotMarkets } from './spotMarkets/useUniqueSpotMarkets';
import { useTokenAccountsStore } from 'src/stores/tokenAccounts/useTokenAccountsStore';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useCurrentWalletAdapter from 'src/hooks/useCurrentWalletAdapter';

export default function useTotalWalletQuoteBalance(): {
	totalQuoteBalance: BigNum;
	isLoading: boolean;
} {
	const uniqueSpotMarkets = useUniqueSpotMarkets();
	const getOraclePrice = useGetOraclePriceForMarket();
	const solBalance = useVelocityStore((s) => s.wallet.currentSolBalance);
	const wallet = useCurrentWalletAdapter();
	const lastSyncedWallet = useTokenAccountsStore((s) => s.lastSyncedWallet);
	const tokenAccounts = useTokenAccountsStore((s) => s.tokenAccounts);

	const isLoading = lastSyncedWallet !== wallet?.publicKey?.toBase58();

	const totalQuoteBalance = useMemo(() => {
		return uniqueSpotMarkets.reduce((sum, market) => {
			const isSol = market.mint.equals(WRAPPED_SOL_MINT);

			const balance = isSol
				? solBalance
				: BigNum.fromPrint(
						(
							tokenAccounts[market.mint.toBase58()]?.parsedBalance ?? 0
						).toString(),
						market.precisionExp
					);

			const oraclePrice =
				getOraclePrice(MarketId.createSpotMarket(market.marketIndex)) ??
				BigNum.zero(PRICE_PRECISION_EXP);

			const quoteValue = balance
				.shiftTo(PRICE_PRECISION_EXP)
				.mul(oraclePrice)
				.shiftTo(QUOTE_PRECISION_EXP);

			return sum.add(quoteValue);
		}, BigNum.zero(QUOTE_PRECISION_EXP));
	}, [uniqueSpotMarkets, solBalance, tokenAccounts, getOraclePrice]);

	return {
		totalQuoteBalance,
		isLoading,
	};
}
